import axios, { AxiosError, type AxiosInstance } from 'axios'

const TOKEN_STORAGE_KEY = 'aios_jwt'

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function clearToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/** Persist the access token so the request interceptor attaches it as a Bearer. */
export function storeToken(token: string) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } catch {
    // ignore
  }
}

/** True when a token is present — lets the Login page skip a redundant sign-in. */
export function hasToken(): boolean {
  return Boolean(getStoredToken())
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
})

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearToken()
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

export type OverviewResponse = {
  servicesTotal: number
  containersRunning: number
  containersTotal: number
  hostsUp: number
  hostsTotal?: number
  certsExpiringSoon: number
}

export type Host = {
  id: string
  name: string
  provider: string
  region: string
  ip: string
  role: string
  cpu_cores: number
  memory_gb: number
  disk_gb: number
  ssh: { enabled: boolean; port: number; user: string }
  health: {
    reachable: boolean
    probe: 'tcp' | 'http' | 'none'
    port?: number
    path?: string
    durationMs?: number
    error?: string
  }
}

export type Certificate = {
  domain: string
  path: string
  issuer?: string
  expires_at?: string
  days_remaining?: number
  status: 'ok' | 'expiring_soon' | 'expired' | 'unreadable'
  error?: string
}

export type DnsRecord = {
  hostname: string
  type: string
  value: string
  ttl?: number
  provider?: string
  purpose?: string
}

export const controlCenterApi = {
  getOverview: () =>
    apiClient.get<OverviewResponse>('/control-center/overview').then((r) => r.data),
  listServices: () => apiClient.get('/platform/services').then((r) => r.data),
  getInfrastructure: () => apiClient.get('/platform/infrastructure').then((r) => r.data),
  getTopology: () => apiClient.get('/control-center/topology').then((r) => r.data),
  listServers: () => apiClient.get('/control-center/servers').then((r) => r.data),
  listNetworks: () => apiClient.get('/control-center/networks').then((r) => r.data),
  listCertificates: () =>
    apiClient.get('/control-center/certificates').then((r) => r.data),
  listDns: () => apiClient.get('/control-center/dns').then((r) => r.data),
  listAgentRegistry: () => apiClient.get('/platform/agent-registry').then((r) => r.data),
  listMcpToolCatalog: () => apiClient.get('/mcp/tools/catalog').then((r) => r.data),
  listMcpInvocations: () => apiClient.get('/mcp/invocations').then((r) => r.data),
  getMcpInvocationTrace: (id: string) =>
    apiClient.get(`/mcp/invocations/${id}/trace`).then((r) => r.data),
  retryMcpInvocation: (id: string) =>
    apiClient.post(`/mcp/invocations/${id}/retry`).then((r) => r.data),
  cancelMcpInvocation: (id: string) =>
    apiClient.post(`/mcp/invocations/${id}/cancel`).then((r) => r.data),
}
